import { useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import {
  addDays,
  OBLIGATION_BASES,
  OBLIGATION_BASIS_LABELS,
  OBLIGATION_KIND_LABELS,
  OBLIGATION_KINDS,
  parseIntervalInput,
  todayIso,
  type ObligationBasis,
  type ObligationKind,
} from "@klantpunt/shared";
import { Segmented } from "../../app/Choice";
import { Modal } from "../../app/Modal";
import { api, ApiError } from "../../lib/api";
import { can, hasModule, useMe } from "../../lib/auth";
import { useToast } from "../../lib/toast";
import { useBuilding } from "../building/useBuilding";
import { NO_PARTY, PartyPicker, type Party } from "../directory/PartyPicker";
import { BUILDING_OWNER } from "../obligations/BuildingObligationsPanel";
import { obligationsPath } from "../obligations/useObligations";
import { usePeople } from "../tasks/useTasks";

type Mode = "asset" | "taak";
const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : "Er ging iets mis");

/** Wat de gebruiker mag toevoegen: uitvoering bij een asset (Assets bewerken) en/of een taak. */
export function useAddModes(): Mode[] {
  const { data: me } = useMe();
  return [
    can(me, "asset.manage") && hasModule(me, "assets", "edit") && ("asset" as const),
    can(me, "task.manage") && hasModule(me, "issues", "edit") && ("taak" as const),
  ].filter((m): m is Mode => Boolean(m));
}

/**
 * Regel toevoegen vanuit de planning (planning stap 3, deel 2): keuring of onderhoud bij een asset (komt ook bij
 * de asset en in het MJOP) of een taak met een uiterste datum. Met *Toevoegen en inplannen* opent daarna het
 * venster om meteen een afspraak te maken.
 */
export function AddLineDialog({
  buildings,
  buildingId: initialBuilding,
  onClose,
  onAdded,
}: {
  buildings: { id: string; code: string; name: string }[];
  buildingId: string | null;
  onClose: () => void;
  /** Regel-id in de planning (`o:`/`t:`) en of het inplanvenster moet openen. */
  onAdded: (lineId: string, plan: boolean) => void;
}) {
  const modes = useAddModes();
  const toast = useToast();
  const qc = useQueryClient();
  const [mode, setMode] = useState<Mode>(modes[0] ?? "taak");
  const [buildingId, setBuildingId] = useState(initialBuilding ?? buildings[0]?.id ?? "");
  const building = useBuilding(buildingId);
  const assets = (building.data?.assets ?? []).slice().sort((a, b) => a.code.localeCompare(b.code, "nl"));
  const [assetId, setAssetId] = useState("");
  const [name, setName] = useState("");
  const [kind, setKind] = useState<ObligationKind>("onderhoud");
  const [basis, setBasis] = useState<ObligationBasis>("eigen");
  const [interval, setInterval] = useState("1 jr");
  const [due, setDue] = useState(addDays(todayIso(), 42));
  const [who, setWho] = useState<Party>(NO_PARTY);
  const people = usePeople();
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const iv = parseIntervalInput(interval);

  async function save(plan: boolean) {
    const errs: Record<string, string> = {};
    if (!buildingId) errs.building = "Kies een gebouw";
    if (mode === "asset" && !assetId) errs.asset = "Kies een asset of het gebouw";
    if (!name.trim()) errs.name = "Vul een omschrijving in";
    if (mode === "asset" && !iv) errs.interval = "Interval als 1 wk, 6 mnd, 1 jr of eenmalig";
    if (!due) errs.due = "Kies een datum";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    const id = crypto.randomUUID();
    try {
      if (mode === "asset") {
        await api(obligationsPath(buildingId, assetId === BUILDING_OWNER ? null : assetId), {
          method: "POST",
          body: {
            id,
            name: name.trim(),
            kind,
            basis,
            intervalMonths: iv!.months,
            intervalWeeks: iv!.weeks,
            nextDueOn: due,
            performerCompanyId: who.companyId,
            performerPersonId: who.personId,
            performer: who.companyId ? null : who.text.trim() || null,
          },
        });
        void qc.invalidateQueries({ queryKey: ["asset"] });
      } else {
        await api(`/buildings/${buildingId}/tasks`, {
          method: "POST",
          body: {
            id,
            title: name.trim(),
            dueDate: due,
            assigneeUserId: who.userId,
            assigneeCompanyId: who.companyId,
            assigneePersonId: who.personId,
            assigneeText: who.text.trim() || null,
          },
        });
        void qc.invalidateQueries({ queryKey: ["tasks"] });
      }
      await qc.invalidateQueries({ queryKey: ["planning"] });
      toast(`${name.trim()} toegevoegd`);
      onAdded(`${mode === "asset" ? "o" : "t"}:${id}`, plan);
    } catch (e) {
      toast(errMsg(e));
      setBusy(false);
    }
  }

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void save(false);
  };
  const err = (k: string) => errors[k] && <span className="hint err">{errors[k]}</span>;

  return (
    <Modal
      title="Regel toevoegen"
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button type="button" className="btn" disabled={busy} onClick={() => void save(true)}>
            Toevoegen en inplannen
          </button>
          <button type="submit" form="addline" className="btn primary" disabled={busy}>
            Toevoegen
          </button>
        </>
      }
    >
      <form id="addline" className="stack" onSubmit={submit} noValidate>
        {modes.length > 1 && (
          <div className="field">
            <span className="label" aria-hidden="true">
              Wat voeg je toe?
            </span>
            <Segmented<Mode>
              label="Wat voeg je toe?"
              value={mode}
              onChange={(m) => {
                setMode(m);
                if (who.userId) setWho(NO_PARTY);
              }}
              items={[
                { value: "asset", label: "Keuring of onderhoud" },
                { value: "taak", label: "Taak" },
              ]}
            />
            <span className="hint">
              {mode === "asset"
                ? "Met een interval, bij een asset of bij het gebouw zelf. Staat daar ook onder Onderhoud en keuringen, en in het MJOP."
                : "Eenmalig werk met een uiterste datum. Verdwijnt uit de planning als de taak klaar is."}
            </span>
          </div>
        )}
        <label className="field">
          <span className="label">Gebouw *</span>
          <select
            value={buildingId}
            aria-invalid={errors.building ? true : undefined}
            onChange={(e) => {
              setBuildingId(e.target.value);
              setAssetId("");
              setWho(NO_PARTY);
            }}
          >
            {!buildingId && <option value="">Kies een gebouw</option>}
            {buildings.map((b) => (
              <option key={b.id} value={b.id}>
                {b.code} {b.name}
              </option>
            ))}
          </select>
          {err("building")}
        </label>
        {mode === "asset" && (
          <label className="field">
            <span className="label">Asset of gebouw *</span>
            <select
              value={assetId}
              aria-invalid={errors.asset ? true : undefined}
              onChange={(e) => setAssetId(e.target.value)}
            >
              <option value="">{building.isPending ? "Laden…" : "Kies een asset of het gebouw"}</option>
              <option value={BUILDING_OWNER}>Het gebouw zelf (geen asset)</option>
              {assets.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code} {a.name}
                </option>
              ))}
            </select>
            {err("asset")}
          </label>
        )}
        <label className="field">
          <span className="label">Omschrijving *</span>
          <input
            value={name}
            maxLength={mode === "asset" ? 200 : 120}
            placeholder={mode === "asset" ? "bijv. Filters vervangen" : "bijv. Dakgoten reinigen"}
            aria-invalid={errors.name ? true : undefined}
            onChange={(e) => setName(e.target.value)}
          />
          {err("name")}
        </label>
        {mode === "asset" && (
          <div className="formgrid">
            <label className="field">
              <span className="label">Soort</span>
              <select value={kind} onChange={(e) => setKind(e.target.value as ObligationKind)}>
                {OBLIGATION_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {OBLIGATION_KIND_LABELS[k]}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="label">Grondslag</span>
              <select value={basis} onChange={(e) => setBasis(e.target.value as ObligationBasis)}>
                {OBLIGATION_BASES.map((k) => (
                  <option key={k} value={k}>
                    {OBLIGATION_BASIS_LABELS[k]}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="label">Interval</span>
              <input
                value={interval}
                aria-invalid={errors.interval ? true : undefined}
                onChange={(e) => setInterval(e.target.value)}
              />
              {errors.interval ? err("interval") : <span className="hint">1 wk, 6 mnd, 1 jr of eenmalig</span>}
            </label>
          </div>
        )}
        <label className="field">
          <span className="label">{mode === "asset" ? "Eerste vervaldatum *" : "Uiterlijk klaar *"}</span>
          <input
            type="date"
            value={due}
            aria-invalid={errors.due ? true : undefined}
            onChange={(e) => setDue(e.target.value)}
          />
          {err("due")}
        </label>
        <PartyPicker
          id="add-performer"
          label="Uitvoerder"
          value={who}
          onChange={setWho}
          buildingId={buildingId || undefined}
          users={mode === "taak" ? people.data?.map((p) => ({ id: p.userId, name: p.name })) : undefined}
          emptyLabel={mode === "taak" ? "Nog niet toegewezen" : "Nog niet bekend"}
        />
      </form>
    </Modal>
  );
}
